/**
 * Organism – PunchOutGuardModal
 * Shown when HR tries to punch out without submitting today's report.
 * Contains an inline ReportForm; on successful submit it calls onReportSubmitted
 * so the parent can retry the punch-out.
 *
 * Props:
 *  - isOpen              bool
 *  - onClose             () => void   (only available if skipable; here it's not – guard is strict)
 *  - onReportSubmitted   () => void   called after the report is saved
 */
import React, { useEffect } from 'react';
import { MdClose, MdAssignment, MdWarning } from 'react-icons/md';
import ReportForm from '../molecules/ReportForm';

const PunchOutGuardModal = ({ isOpen, onClose, onReportSubmitted }) => {
  // Lock scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="punch-out-guard-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-charcoal/50 backdrop-blur-sm" />

      {/* Panel */}
      <div className="relative w-full max-w-lg max-h-[90vh] flex flex-col bg-surface rounded-2xl shadow-lg overflow-hidden animate-slideUp">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border-light bg-status-error-bg">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-status-error/15 flex items-center justify-center">
            <MdWarning className="text-status-error" size={20} />
          </div>
          <div className="flex-1">
            <h2
              id="punch-out-guard-title"
              className="text-base font-semibold text-status-error"
            >
              Daily Report Required
            </h2>
            <p className="text-xs text-text-secondary mt-0.5">
              You must submit today's report before punching out.
            </p>
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-text-tertiary hover:bg-background transition-colors"
              aria-label="Close"
            >
              <MdClose size={16} />
            </button>
          )}
        </div>

        {/* Body – scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-accent-green/10 border border-accent-green/25">
            <MdAssignment className="text-accent-green flex-shrink-0" size={18} />
            <p className="text-sm text-text-primary">
              Fill in the form below. After submitting, your punch-out will be processed automatically.
            </p>
          </div>

          <ReportForm
            compact
            onSuccess={onReportSubmitted}
          />
        </div>
      </div>
    </div>
  );
};

export default PunchOutGuardModal;
